import { useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Spin, Typography, Alert } from 'antd';
import { useScanLogin } from '../../hooks/useScanLogin';
import { useSession } from '../../stores/session';

const { Title, Text } = Typography;

export default function LoginPage() {
  // Phase 1-2 review A3: early-return for already-authenticated users to avoid
  // a one-frame render of LoginPage + an extra /qrcode request that wastes a nonce.
  const accessToken = useSession((s) => s.accessToken);
  if (accessToken) {
    return <Navigate to="/" replace />;
  }
  return <LoginPageInner />;
}

function LoginPageInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthCode = searchParams.get('code');
  const oauthState = searchParams.get('state');
  const { pollResult, loading, refresh } = useScanLogin(oauthCode, oauthState);
  const setSession = useSession((s) => s.setSession);
  const isCallback = Boolean(oauthCode && oauthState);

  // Persist tokens and redirect on confirmed scan
  useEffect(() => {
    if (pollResult.status === 'confirmed' && pollResult.data) {
      const { accessToken: at, actorId, role } = pollResult.data;
      setSession({ actorId, role, accessToken: at });
      navigate('/', { replace: true });
    }
  }, [pollResult, setSession, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f2f5',
      }}
    >
      <Card style={{ width: 360, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          经年叙 · 运营后台
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          用微信扫码授权登录
        </Text>

        {loading && (
          <div style={{ padding: 40 }}>
            <Spin size="large" />
            <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
              {isCallback ? '正在完成微信登录…' : '正在跳转微信…'}
            </Text>
          </div>
        )}

        {!loading && !isCallback && pollResult.status !== 'error' && pollResult.status !== 'expired' && (
          <div>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 16, fontSize: 13 }}
            >
              点击后跳转微信开放平台，使用普通微信扫码确认身份。
            </Text>
            <Button type="primary" size="large" onClick={refresh} block>
              微信扫码登录
            </Button>
          </div>
        )}

        {pollResult.status === 'expired' && (
          <div>
            <Alert message="登录请求已过期" type="warning" showIcon style={{ marginBottom: 16 }} />
            <Button type="primary" onClick={refresh}>
              重新登录
            </Button>
          </div>
        )}

        {pollResult.status === 'error' && (
          <div>
            <Alert
              message={pollResult.error ?? '账号无权访问后台，请联系管理员'}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
            {pollResult.unionId && (
              <Alert
                message="请将此 unionId 加入 admin_users 白名单"
                description={pollResult.unionId}
                type="info"
                style={{ marginBottom: 16, textAlign: 'left' }}
              />
            )}
            <Button onClick={refresh}>重新登录</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
